import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { ArrowRight, ArrowUpRight, Target, UserRound } from 'lucide-react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { CandidateAvatar } from '@/components/common/CandidateAvatar/CandidateAvatar';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { PoliticalMatchResultHero } from '@/components/political-match/PoliticalMatchResultHero/PoliticalMatchResultHero';
import { buildAnalyticsEventName, trackAnalyticsEvent } from '@/lib/analytics';
import { usePoliticalMatchMatches } from '@/hooks/usePoliticalMatchMatches';
import { usePoliticalMatchFlow } from '@/hooks/usePoliticalMatchFlow';
import { usePoliticalMatchUserPhoto } from '@/hooks/usePoliticalMatchUserPhoto';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { buildCandidatePhotoPath } from '@/utils/candidatePhoto';
import { classNames } from '@/utils/classNames';
import {
  buildPoliticalMatchStoryCopy,
  calculatePoliticalMatchDistance,
  clampPoliticalMatchDiagramCoordinate,
  politicalMatchCloseCandidateDistanceLimit,
  sortPoliticalMatchMatchesByDistance,
} from '@/utils/political-match';

import styles from '@/styles/positions.module.css';

function formatPoliticalMatchPercent(score: number | null): string {
  if (score === null) {
    return 'Sem posição suficiente';
  }

  return `${Math.round(((score + 2) / 4) * 100)}/100`;
}

export function PositionsSummaryPage(): JSX.Element {
  const { state, questionPlan, answeredQuestionCount, restartFlow, status, error } = usePoliticalMatchFlow();
  const matchesState = usePoliticalMatchMatches();
  const navigate = useNavigate();
  const effectiveMatches = state.matches?.length ? state.matches : matchesState.matches;
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const { userPhotoUrl, setUserPhotoUrl } = usePoliticalMatchUserPhoto();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const summary = useMemo(() => {
    if (!state.mode) {
      return null;
    }

    return {
      economicScore: state.economicScore ?? null,
      personalScore: state.personalScore ?? null,
      answeredQuestionCount,
      totalQuestions: questionPlan.length,
    };
  }, [answeredQuestionCount, questionPlan.length, state.economicScore, state.mode, state.personalScore]);

  const storyCopy = useMemo(
    () => buildPoliticalMatchStoryCopy(summary?.economicScore ?? null, summary?.personalScore ?? null),
    [summary?.economicScore, summary?.personalScore],
  );

  const orderedMatches = useMemo(() => {
    return sortPoliticalMatchMatchesByDistance(effectiveMatches, summary?.economicScore ?? null, summary?.personalScore ?? null);
  }, [effectiveMatches, summary]);

  const closestCandidateDistance = useMemo(() => {
    if (!summary || orderedMatches.length === 0) {
      return null;
    }

    const closestMatch = orderedMatches[0];

    if (!closestMatch) {
      return null;
    }

    return calculatePoliticalMatchDistance(
      summary.economicScore,
      summary.personalScore,
      closestMatch.position.economicScore,
      closestMatch.position.personalScore,
    );
  }, [orderedMatches, summary]);

  const hasCloseCandidate = closestCandidateDistance !== null && closestCandidateDistance <= politicalMatchCloseCandidateDistanceLimit;

  const titleNote =
    closestCandidateDistance !== null && !hasCloseCandidate
      ? {
          message: 'Seu mapa está à frente do elenco atual',
          tip: 'Isso quer dizer que, entre os candidatos carregados, nenhum ficou perto o bastante do seu posicionamento para ser uma correspondência forte.',
        }
      : undefined;

  const topCandidateMatches = orderedMatches.slice(0, 3);

  const selectedCandidateMatch = useMemo(() => {
    if (orderedMatches.length === 0) {
      return null;
    }

    if (selectedCandidateId) {
      return orderedMatches.find((entry) => entry.candidate.id === selectedCandidateId) ?? orderedMatches[0] ?? null;
    }

    return orderedMatches[0] ?? null;
  }, [orderedMatches, selectedCandidateId]);

  const selectedCandidateRank = selectedCandidateMatch ? orderedMatches.findIndex((match) => match.candidate.id === selectedCandidateMatch.candidate.id) + 1 : 0;
  const selectedCandidateRankLabel = selectedCandidateRank > 0 ? `${selectedCandidateRank}º mais próximo` : null;

  const candidateOptions = useMemo(
    () =>
      orderedMatches.map((match) => ({
        id: match.candidate.id,
        label: match.candidate.name,
        photoPath: buildCandidatePhotoPath(match.candidate.slug),
      })),
    [orderedMatches],
  );

  const handleUserPhotoChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setUserPhotoUrl(typeof reader.result === 'string' ? reader.result : null);
    };

    reader.readAsDataURL(file);
  };

  const selectedCandidatePhotoUrl = selectedCandidateMatch ? buildCandidatePhotoPath(selectedCandidateMatch.candidate.slug) : null;

  useEffect(() => {
    if (orderedMatches.length === 0) {
      setSelectedCandidateId(null);
      return;
    }

    setSelectedCandidateId((currentSelectedCandidateId) => {
      if (currentSelectedCandidateId && orderedMatches.some((match) => match.candidate.id === currentSelectedCandidateId)) {
        return currentSelectedCandidateId;
      }

      return orderedMatches[0]?.candidate.id ?? null;
    });
  }, [orderedMatches]);

  const canNavigateCandidates = topCandidateMatches.length > 1;

  const currentTopCandidateIndex = useMemo(() => {
    if (!selectedCandidateMatch) {
      return 0;
    }

    const index = topCandidateMatches.findIndex((match) => match.candidate.id === selectedCandidateMatch.candidate.id);

    return index >= 0 ? index : 0;
  }, [selectedCandidateMatch, topCandidateMatches]);

  const goToPreviousCandidate = (): void => {
    if (!canNavigateCandidates) {
      return;
    }

    const nextIndex = (currentTopCandidateIndex - 1 + topCandidateMatches.length) % topCandidateMatches.length;
    setSelectedCandidateId(topCandidateMatches[nextIndex]?.candidate.id ?? null);
  };

  const goToNextCandidate = (): void => {
    if (!canNavigateCandidates) {
      return;
    }

    const nextIndex = (currentTopCandidateIndex + 1) % topCandidateMatches.length;
    setSelectedCandidateId(topCandidateMatches[nextIndex]?.candidate.id ?? null);
  };

  const selectCandidate = (candidateId: string): void => {
    setSelectedCandidateId(candidateId);
  };

  const candidateDetailPath = selectedCandidateMatch ? `/match-politico/detalhes/${selectedCandidateMatch.candidate.id}` : '/match-politico/resumo';
  const shareResultPath = selectedCandidateMatch
    ? `/match-politico/compartilhar?candidateId=${encodeURIComponent(selectedCandidateMatch.candidate.id)}`
    : '/match-politico/compartilhar';

  const handleRestartQuestionnaire = (): void => {
    restartFlow();
    navigate('/match-politico');
  };

  const diagramPoint = useMemo(() => {
    if (!summary) {
      return null;
    }

    const leftValue = clampPoliticalMatchDiagramCoordinate(summary.economicScore, 'x');
    const topValue = clampPoliticalMatchDiagramCoordinate(summary.personalScore, 'y');

    return {
      left: `${leftValue}%`,
      top: `${topValue}%`,
      label: summary.economicScore === null && summary.personalScore === null ? 'Sem posição suficiente' : 'Você',
      placement: topValue < 50 ? ('below' as const) : ('above' as const),
    };
  }, [summary]);

  const selectedCandidatePoint = useMemo(() => {
    if (!selectedCandidateMatch) {
      return null;
    }

    const leftValue = clampPoliticalMatchDiagramCoordinate(selectedCandidateMatch.position.economicScore, 'x');
    const topValue = clampPoliticalMatchDiagramCoordinate(selectedCandidateMatch.position.personalScore, 'y');

    return {
      left: `${leftValue}%`,
      top: `${topValue}%`,
      label: selectedCandidateMatch.candidate.name,
      placement: topValue < 50 ? ('below' as const) : ('above' as const),
    };
  }, [selectedCandidateMatch]);

  useSeo({
    title: buildPageTitle('Resultado do Match Político'),
    description: 'Veja seu posicionamento no mapa político e explore os candidatos, temas e fontes oficiais.',
    canonicalPath: '/match-politico/resumo',
    indexable: false,
  });

  if (status === 'loading') {
    return <LoadingState title="Carregando resultado" messages={['Consultando seu progresso', 'Preparando o resumo', 'Montando os candidatos']} />;
  }

  if (status === 'error') {
    return <EmptyState tone="danger" title="Não foi possível carregar o resultado" description={error ?? ''} />;
  }

  if (!state.mode) {
    return <Navigate to="/match-politico" replace />;
  }

  if (matchesState.status === 'loading' && !state.matches?.length) {
    return <LoadingState title="Carregando candidatos" messages={['Consultando candidatos', 'Preparando o resultado', 'Montando os cards']} />;
  }

  if (matchesState.status === 'error' && !state.matches?.length) {
    return <EmptyState tone="danger" title="Não foi possível carregar os candidatos" description={matchesState.error ?? ''} />;
  }

  return (
    <section className={styles.questionCard}>
      <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado' }]} />

      {summary ? (
        <div className={styles.summary}>
          <PoliticalMatchResultHero
            title={storyCopy.title}
            description={storyCopy.description}
            titleNote={titleNote}
            candidateName={hasCloseCandidate ? selectedCandidateMatch?.candidate.name ?? 'Seu match mais próximo' : 'Nenhum candidato próximo'}
            candidatePhotoUrl={hasCloseCandidate ? selectedCandidatePhotoUrl : null}
            showCandidatePhoto={hasCloseCandidate}
            userPhotoUrl={userPhotoUrl}
            userPhotoAction={{
              label: userPhotoUrl ? 'Trocar minha foto' : 'Adicionar minha foto',
              onClick: () => fileInputRef.current?.click(),
            }}
            candidateRankLabel={hasCloseCandidate ? selectedCandidateRankLabel ?? undefined : undefined}
            candidateNavigation={
              hasCloseCandidate && selectedCandidateMatch
                ? {
                  onPrevious: goToPreviousCandidate,
                  onNext: goToNextCandidate,
                  previousDisabled: !canNavigateCandidates,
                  nextDisabled: !canNavigateCandidates,
                }
                : undefined
            }
            resultActions={{
              share: {
                label: 'Compartilhar meu resultado',
                to: shareResultPath,
              },
              restart: {
                label: 'Refazer questionário',
                onClick: handleRestartQuestionnaire,
              },
            }}
            className={styles.summaryStoryHero}
          />

          <input ref={fileInputRef} className={styles.shareUploadInput} type="file" accept="image/*" onChange={handleUserPhotoChange} />

          <section className={styles.summaryHero}>
            <div className={styles.summaryResultsHeader}>
              <p className={styles.summaryResultsTitle}>Detalhes complementares</p>
              <Button to="/match-politico/metodologia" variant="ghost" size="sm" className={styles.summaryHowItWorksLink}>
                Como funciona?
              </Button>
            </div>

            <div className={styles.summaryHeroGrid}>
              <Card className={styles.summaryHeroCard}>
                <div className={styles.summaryDiagramCard}>
                  <div className={styles.summaryDiagramCopy}>
                    <p className={styles.summaryDiagramText}>Seu ponto e o candidato mais próximo aparecem aqui para comparação espacial.</p>
                    <p className={styles.summaryDiagramHint}>O diagrama fica como leitura complementar do resultado principal.</p>
                  </div>

                  <div className={styles.summaryDiagramLayout}>
                    <div className={styles.summaryDiagramChart} role="img" aria-label="Diagrama do seu posicionamento">
                      <span className={styles.summaryDiagramAxisVertical} />
                      <span className={styles.summaryDiagramAxisHorizontal} />
                      <span className={styles.summaryDiagramOrigin} />
                      <span className={styles.summaryDiagramTopLabel}>Mais liberdade pessoal</span>
                      <span className={styles.summaryDiagramRightLabel}>Mais liberdade econômica</span>
                      <span className={styles.summaryDiagramBottomLabel}>Menos liberdade pessoal</span>
                      <span className={styles.summaryDiagramLeftLabel}>Menos liberdade econômica</span>

                      {diagramPoint ? (
                        <span
                          className={classNames(
                            styles.summaryDiagramPoint,
                            diagramPoint.placement === 'below' ? styles.summaryDiagramPointBelow : styles.summaryDiagramPointAbove,
                          )}
                          style={{ left: diagramPoint.left, top: diagramPoint.top }}
                        >
                          <span className={styles.summaryDiagramPointDot} />
                          <span
                            className={classNames(
                              styles.summaryDiagramPointBubble,
                              diagramPoint.placement === 'below' ? styles.summaryDiagramPointBubbleBelow : styles.summaryDiagramPointBubbleAbove,
                            )}
                          >
                            {diagramPoint.label}
                          </span>
                        </span>
                      ) : null}

                      {selectedCandidatePoint ? (
                        <span
                          className={classNames(
                            styles.summaryDiagramCandidatePoint,
                            selectedCandidatePoint.placement === 'below' ? styles.summaryDiagramCandidatePointBelow : styles.summaryDiagramCandidatePointAbove,
                          )}
                          style={{ left: selectedCandidatePoint.left, top: selectedCandidatePoint.top }}
                          title={selectedCandidatePoint.label}
                        >
                          <span className={styles.summaryDiagramCandidatePointDot} />
                          <span className={styles.summaryDiagramCandidatePointBubble}>{selectedCandidatePoint.label}</span>
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {candidateOptions.length > 0 ? (
                    <div className={styles.summaryCandidateSection}>
                      <p className={styles.summaryCandidateStripLabel}>Selecione o candidato para vê-lo no diagrama</p>
                      <p className={styles.summaryDiagramHint}>
                        Os avatares abaixo estão ordenados da menor para a maior distância euclidiana até a sua posição.
                      </p>
                      <div className={styles.summaryCandidateStrip} role="group" aria-label="Selecionar candidato no mapa">
                        {candidateOptions.map((candidate) => {
                          const isSelected = candidate.id === selectedCandidateMatch?.candidate.id;

                          return (
                            <button
                              key={candidate.id}
                              type="button"
                              className={classNames(styles.summaryCandidateButton, isSelected && styles.summaryCandidateButtonActive)}
                              onClick={() => {
                                trackAnalyticsEvent(buildAnalyticsEventName(`Selecionar ${candidate.label}`, `match:summary:candidate:${candidate.id}`));
                                selectCandidate(candidate.id);
                              }}
                              aria-label={`Selecionar ${candidate.label}`}
                              aria-pressed={isSelected}
                            >
                              <CandidateAvatar
                                alt={candidate.label}
                                imageUrl={candidate.photoPath}
                                name={candidate.label}
                                size="sm"
                                className={styles.summaryCandidateAvatar}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>

              <div className={styles.summaryResultsColumn}>
                <Card className={styles.summaryResultCard}>
                  <div className={styles.summaryResultIcon}>
                    <ArrowUpRight size={28} />
                  </div>
                  <div className={styles.summaryResultBody}>
                    <p className={styles.summaryResultLabel}>Dinheiro e trabalho</p>
                    <p className={styles.summaryResultValue}>{formatPoliticalMatchPercent(summary.economicScore)}</p>
                  </div>
                  <Badge tone="success">Alta</Badge>
                </Card>

                <Card className={styles.summaryResultCard}>
                  <div className={styles.summaryResultIcon}>
                    <UserRound size={28} />
                  </div>
                  <div className={styles.summaryResultBody}>
                    <p className={styles.summaryResultLabel}>Vida pessoal</p>
                    <p className={styles.summaryResultValue}>{formatPoliticalMatchPercent(summary.personalScore)}</p>
                  </div>
                  <Badge tone="success">Alta</Badge>
                </Card>

                <Button to={candidateDetailPath} variant="primary" size="lg" className={styles.summaryCta} fullWidth disabled={!selectedCandidateMatch}>
                  <Target size={20} />
                  Entender posição dos candidatos
                  <ArrowRight size={20} />
                </Button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
